import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { Network, NetworkSelectionState } from '../types';
import { NETWORKS, getNetworkCondition, shouldRedirectToSales, getSalesRedirectUrl, getConditionalMessage } from '../config/networks';

interface MultiNetworkSelectionFieldProps {
  values: string[];
  onChange: (networkIds: string[], networkNames: string[]) => void;
  onNetworkCondition?: (state: NetworkSelectionState) => void;
  otherNetworkName?: string;
  onOtherNetworkNameChange?: (name: string) => void;
  error?: string;
  otherNetworkError?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
}

export const MultiNetworkSelectionField: React.FC<MultiNetworkSelectionFieldProps> = ({
  values,
  onChange,
  onNetworkCondition,
  otherNetworkName = '',
  onOtherNetworkNameChange,
  error,
  otherNetworkError,
  className,
  required = false,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNetworks, setSelectedNetworks] = useState<Network[]>([]);
  const [showCondition, setShowCondition] = useState(false);
  const [conditionalMessage, setConditionalMessage] = useState<string>('');
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Find selected networks from values
  useEffect(() => {
    const networks = values.map(id => NETWORKS.find(n => n.id === id)).filter(Boolean) as Network[];
    setSelectedNetworks(networks);
  }, [values]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Filter networks based on search term
  const filteredNetworks = NETWORKS.filter(network =>
    network.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    network.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (network.description && network.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleNetworkToggle = (network: Network) => {
    // Single select - replace any existing selection
    const newSelectedNetworks: Network[] = [network];

    setSelectedNetworks(newSelectedNetworks);

    // Update parent component
    const networkIds = newSelectedNetworks.map(n => n.id);
    const networkNames = newSelectedNetworks.map(n => n.name);
    onChange(networkIds, networkNames);

    // Close dropdown after selection
    setIsOpen(false);

    // Check for conditional behaviors on the newly selected network
    checkNetworkConditions(network, newSelectedNetworks);
  };

  const checkNetworkConditions = (network: Network, allSelected: Network[]) => {
    const condition = getNetworkCondition(network.id);
    const message = getConditionalMessage(network.id);
    
    if (shouldRedirectToSales(network.id)) {
      // Show redirect message and countdown
      setConditionalMessage(message || 'Redirecting you to our sales team...');
      setShowCondition(true);
      
      // Start countdown for redirect
      let countdown = 5;
      setRedirectCountdown(countdown);
      
      const countdownInterval = setInterval(() => {
        countdown -= 1;
        setRedirectCountdown(countdown);
        
        if (countdown <= 0) {
          clearInterval(countdownInterval);
          const redirectUrl = getSalesRedirectUrl(network.id);
          if (redirectUrl) {
            window.location.href = redirectUrl;
          }
        }
      }, 1000);
      
      // Update parent component about the condition
      updateParentCondition(allSelected, true, condition, false);
    } else if (network.requiresApproval || message) {
      // Show approval/information message
      setConditionalMessage(message || 'Some selected networks have special requirements.');
      setShowCondition(true);
      
      // Update parent component
      updateParentCondition(allSelected, true, condition, true);
    } else {
      // Check if any other selected networks have conditions
      updateConditionalState(allSelected);
    }
  };

  const updateConditionalState = (allSelected: Network[]) => {
    // Check if any selected networks have special conditions
    const networksWithConditions = allSelected.filter(n => 
      shouldRedirectToSales(n.id) || n.requiresApproval || getConditionalMessage(n.id)
    );
    
    if (networksWithConditions.length > 0) {
      const hasRedirectNetwork = networksWithConditions.some(n => shouldRedirectToSales(n.id));
      const hasApprovalNetwork = networksWithConditions.some(n => n.requiresApproval);
      
      if (hasRedirectNetwork) {
        const redirectNetwork = networksWithConditions.find(n => shouldRedirectToSales(n.id));
        const message = getConditionalMessage(redirectNetwork!.id);
        setConditionalMessage(message || 'Some selected networks require sales contact.');
        setShowCondition(true);
        updateParentCondition(allSelected, true, null, false);
      } else if (hasApprovalNetwork) {
        setConditionalMessage('Some selected networks require approval.');
        setShowCondition(true);
        updateParentCondition(allSelected, true, null, true);
      } else {
        setConditionalMessage('Some selected networks have special setup requirements.');
        setShowCondition(true);
        updateParentCondition(allSelected, true, null, true);
      }
    } else {
      // No special conditions
      setShowCondition(false);
      setConditionalMessage('');
      updateParentCondition(allSelected, false, null, true);
    }
  };

  const updateParentCondition = (
    networks: Network[], 
    showMessage: boolean, 
    condition: any, 
    canProceed: boolean
  ) => {
    if (onNetworkCondition) {
      onNetworkCondition({
        selectedNetworks: networks,
        showConditionalMessage: showMessage,
        conditionalAction: condition,
        canProceed
      });
    }
  };

  const handleCloseCondition = () => {
    setShowCondition(false);
    setRedirectCountdown(null);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const getSelectionDisplayText = () => {
    if (selectedNetworks.length === 0) {
      return 'Select your network or club';
    }
    return selectedNetworks[0].displayName;
  };

  return (
    <div className={classNames('space-y-2', className)}>
      {/* Field Label */}
      <label className="block text-sm font-medium text-gray-700">
        Network/Club
        {required && <span className="text-error-600 ml-1">*</span>}
      </label>

      {/* Custom Dropdown */}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={classNames(
            'relative w-full bg-white border rounded-md pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm',
            {
              'border-gray-300': !error,
              'border-error-300': error,
              'bg-gray-50 cursor-not-allowed': disabled,
              'hover:border-gray-400': !disabled && !error,
            }
          )}
        >
          <span className="block truncate text-gray-900">
            {getSelectionDisplayText()}
          </span>
          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <svg 
              className={classNames('h-5 w-5 text-gray-400 transition-transform', {
                'transform rotate-180': isOpen
              })} 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </span>
        </button>

        {/* Dropdown Menu */}
        {isOpen && !disabled && (
          <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-80 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-hidden focus:outline-none sm:text-sm">
            {/* Search Input */}
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search networks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 text-sm"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Networks List */}
            <div className="max-h-60 overflow-auto">
              {filteredNetworks.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                  No networks found matching "{searchTerm}"
                </div>
              ) : (
                filteredNetworks.map((network) => {
                  const isSelected = selectedNetworks.some(n => n.id === network.id);
                  return (
                    <button
                      key={network.id}
                      type="button"
                      onClick={() => handleNetworkToggle(network)}
                      className={classNames(
                        'w-full text-left px-4 py-3 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-colors',
                        {
                          'bg-primary-50 text-primary-900': isSelected,
                        }
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 mr-3">
                              <div className={classNames(
                                'w-4 h-4 border-2 rounded-full flex items-center justify-center',
                                isSelected
                                  ? 'border-primary-600'
                                  : 'border-gray-300'
                              )}>
                                {isSelected && (
                                  <div className="w-2 h-2 rounded-full bg-primary-600"></div>
                                )}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 truncate">
                                {network.displayName}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 ml-2">
                          {network.redirectToSales && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap">
                              Sales Contact
                            </span>
                          )}
                          {network.requiresApproval && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 whitespace-nowrap">
                              Approval Required
                            </span>
                          )}
                          {network.customOnboardingPath && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 whitespace-nowrap">
                              Custom Setup
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <p className="text-sm text-error-600 flex items-center">
          <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}

      {/* Conditional Message */}
      {showCondition && conditionalMessage && (
        <div className={classNames(
          'mt-3 p-4 rounded-lg border',
          {
            'bg-blue-50 border-blue-200': selectedNetworks.some(n => shouldRedirectToSales(n.id)),
            'bg-yellow-50 border-yellow-200': selectedNetworks.some(n => n.requiresApproval),
            'bg-green-50 border-green-200': selectedNetworks.some(n => n.customOnboardingPath) && !selectedNetworks.some(n => n.requiresApproval),
            'bg-gray-50 border-gray-200': !selectedNetworks.some(n => shouldRedirectToSales(n.id)) && !selectedNetworks.some(n => n.requiresApproval) && !selectedNetworks.some(n => n.customOnboardingPath)
          }
        )}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              {selectedNetworks.some(n => shouldRedirectToSales(n.id)) ? (
                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                </svg>
              ) : selectedNetworks.some(n => n.requiresApproval) ? (
                <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3 flex-1">
              <p className={classNames(
                'text-sm font-medium',
                {
                  'text-blue-800': selectedNetworks.some(n => shouldRedirectToSales(n.id)),
                  'text-yellow-800': selectedNetworks.some(n => n.requiresApproval),
                  'text-green-800': selectedNetworks.some(n => n.customOnboardingPath) && !selectedNetworks.some(n => n.requiresApproval),
                  'text-gray-800': !selectedNetworks.some(n => shouldRedirectToSales(n.id)) && !selectedNetworks.some(n => n.requiresApproval) && !selectedNetworks.some(n => n.customOnboardingPath)
                }
              )}>
                {selectedNetworks.some(n => shouldRedirectToSales(n.id)) ? 'Sales Contact Required' : 
                 selectedNetworks.some(n => n.requiresApproval) ? 'Approval Required' : 
                 'Special Setup'}
              </p>
              <p className={classNames(
                'mt-1 text-sm',
                {
                  'text-blue-700': selectedNetworks.some(n => shouldRedirectToSales(n.id)),
                  'text-yellow-700': selectedNetworks.some(n => n.requiresApproval),
                  'text-green-700': selectedNetworks.some(n => n.customOnboardingPath) && !selectedNetworks.some(n => n.requiresApproval),
                  'text-gray-700': !selectedNetworks.some(n => shouldRedirectToSales(n.id)) && !selectedNetworks.some(n => n.requiresApproval) && !selectedNetworks.some(n => n.customOnboardingPath)
                }
              )}>
                {conditionalMessage}
              </p>
              
              {/* Redirect Countdown */}
              {redirectCountdown !== null && (
                <div className="mt-2 flex items-center text-sm text-blue-700">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  Redirecting in {redirectCountdown} seconds...
                </div>
              )}
            </div>
            
            {/* Close Button */}
            {!selectedNetworks.some(n => shouldRedirectToSales(n.id)) && (
              <div className="ml-auto pl-3">
                <button
                  type="button"
                  onClick={handleCloseCondition}
                  title="Close message"
                  aria-label="Close network information message"
                  className={classNames(
                    'inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2',
                    {
                      'text-blue-500 hover:bg-blue-100 focus:ring-blue-600': selectedNetworks.some(n => shouldRedirectToSales(n.id)),
                      'text-yellow-500 hover:bg-yellow-100 focus:ring-yellow-600': selectedNetworks.some(n => n.requiresApproval),
                      'text-green-500 hover:bg-green-100 focus:ring-green-600': selectedNetworks.some(n => n.customOnboardingPath) && !selectedNetworks.some(n => n.requiresApproval),
                      'text-gray-500 hover:bg-gray-100 focus:ring-gray-600': !selectedNetworks.some(n => shouldRedirectToSales(n.id)) && !selectedNetworks.some(n => n.requiresApproval) && !selectedNetworks.some(n => n.customOnboardingPath)
                    }
                  )}
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Help Text */}
      <p className="text-sm text-gray-500">
        Select your network or club affiliation. Use the search bar to quickly find your network. Some networks may have special requirements or setup processes.
      </p>

      {/* Other Network Name Input - shown when "Other" is selected */}
      {selectedNetworks.some(n => n.id === 'other') && (
        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Please specify your network <span className="text-gray-500">(Optional)</span>
          </label>
          <input
            type="text"
            value={otherNetworkName}
            onChange={(e) => onOtherNetworkNameChange?.(e.target.value)}
            placeholder="Enter your network name"
            disabled={disabled}
            className={classNames(
              'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm',
              {
                'border-gray-300': true,
                'bg-gray-50 cursor-not-allowed': disabled,
              }
            )}
          />
        </div>
      )}

      {/* Click outside to close dropdown */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};